import { useEffect, useMemo, useState } from "react";
import { Plus, Download, Pencil, Trash2, TrendingUp, Target, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import StatCard from "@/components/StatCard";
import AtividadesVendedorCard from "@/components/AtividadesVendedorCard";
import { listFunil, createFunil, updateFunil, deleteFunil } from "@/lib/api";
import { fmtInt, fmtPct, pct, exportCSV } from "@/lib/format";

const emptyForm = {
  semana: "",
  predios_trabalhados: 0,
  contatos_abordados: 0,
  interessados: 0,
  propostas_enviadas: 0,
  vendas_fechadas: 0,
  meta_semana: 0,
};

export default function FunilTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listFunil();
      setItems(data);
    } catch {
      toast.error("Erro ao carregar funil");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const totalAb = items.reduce((s, i) => s + Number(i.contatos_abordados || 0), 0);
    const totalInt = items.reduce((s, i) => s + Number(i.interessados || 0), 0);
    const totalProp = items.reduce((s, i) => s + Number(i.propostas_enviadas || 0), 0);
    const totalVendas = items.reduce((s, i) => s + Number(i.vendas_fechadas || 0), 0);
    const totalMeta = items.reduce((s, i) => s + Number(i.meta_semana || 0), 0);
    return {
      convAbInt: pct(totalInt, totalAb),
      convPropFech: pct(totalVendas, totalProp),
      totalProp,
      atingimento: pct(totalVendas, totalMeta),
    };
  }, [items]);

  const chartData = useMemo(() =>
    [...items]
      .sort((a, b) => a.semana.localeCompare(b.semana))
      .map((i) => ({
        semana: i.semana.replace(/Semana /, "S").replace(/ - .*/, ""),
        Meta: Number(i.meta_semana || 0),
        Vendas: Number(i.vendas_fechadas || 0),
        Propostas: Number(i.propostas_enviadas || 0),
      })),
  [items]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyForm, ...item }); setOpen(true); };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.semana.trim()) { toast.error("Informe a semana"); return; }
    const payload = {
      ...form,
      predios_trabalhados: Number(form.predios_trabalhados) || 0,
      contatos_abordados: Number(form.contatos_abordados) || 0,
      interessados: Number(form.interessados) || 0,
      propostas_enviadas: Number(form.propostas_enviadas) || 0,
      vendas_fechadas: Number(form.vendas_fechadas) || 0,
      meta_semana: Number(form.meta_semana) || 0,
    };
    try {
      if (editing) { await updateFunil(editing.id, payload); toast.success("Semana atualizada"); }
      else { await createFunil(payload); toast.success("Semana cadastrada"); }
      setOpen(false); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta semana?")) return;
    try { await deleteFunil(id); toast.success("Semana excluída"); load(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const handleExport = () => {
    const rows = items.map((i) => ({
      ...i,
      porcentagem_atingida: pct(i.vendas_fechadas, i.meta_semana).toFixed(1) + "%",
    }));
    exportCSV("funil_semanal.csv", rows, [
      { key: "semana", label: "Semana" },
      { key: "predios_trabalhados", label: "Prédios Trabalhados" },
      { key: "contatos_abordados", label: "Contatos Abordados" },
      { key: "interessados", label: "Interessados" },
      { key: "propostas_enviadas", label: "Propostas Enviadas" },
      { key: "vendas_fechadas", label: "Vendas Fechadas" },
      { key: "meta_semana", label: "Meta da Semana" },
      { key: "porcentagem_atingida", label: "% Atingido" },
    ]);
    toast.success("CSV exportado");
  };

  return (
    <div className="space-y-6">
      {/* Relatório diário/semanal/mensal dos vendedores + ações granulares */}
      <AtividadesVendedorCard />

      {/* Resumo agregado semanal (mantido) */}
      <div className="rounded-xl border border-border bg-card p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-lg">Fechamento semanal consolidado</h3>
          <p className="text-xs text-muted-foreground">Números agregados por semana — visão macro complementar ao relatório detalhado acima.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="kpi-conv-abord-int" label="Abordagem → Interesse" value={fmtPct(totals.convAbInt)} icon={MessageSquare} />
        <StatCard testId="kpi-conv-prop-fech" label="Proposta → Fechamento" value={fmtPct(totals.convPropFech)} icon={CheckCircle2} tone="success" />
        <StatCard testId="kpi-total-propostas" label="Propostas enviadas" value={fmtInt(totals.totalProp)} icon={TrendingUp} tone="warning" />
        <StatCard
          testId="kpi-funil-meta"
          label="Meta acumulada"
          value={fmtPct(totals.atingimento)}
          hint="Vendas fechadas / Meta total"
          icon={Target}
          tone={totals.atingimento >= 100 ? "success" : totals.atingimento >= 50 ? "warning" : "danger"}
        />
      </div>

      {/* Chart */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-funil-vendas">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-semibold text-lg">Meta vs Vendas por semana</h3>
            <p className="text-xs text-muted-foreground">Comparativo semanal de meta, propostas e vendas fechadas.</p>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="semana" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Meta" fill="hsl(var(--chart-3))" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Propostas" fill="hsl(var(--chart-4))" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Vendas" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Toolbar */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-end">
        <Button variant="outline" onClick={handleExport} data-testid="btn-export-csv-funil">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
        <Button onClick={openCreate} data-testid="btn-add-semana">
          <Plus className="w-4 h-4 mr-2" /> Nova semana
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="thin-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Semana</TableHead>
                <TableHead className="text-right">Prédios</TableHead>
                <TableHead className="text-right">Contatos</TableHead>
                <TableHead className="text-right">Interessados</TableHead>
                <TableHead className="text-right">Propostas</TableHead>
                <TableHead className="text-right">Vendas</TableHead>
                <TableHead className="text-right">Meta</TableHead>
                <TableHead className="min-w-[180px]">% Atingido</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Carregando…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Nenhuma semana cadastrada.</TableCell></TableRow>
              )}
              {items.map((i) => {
                const p = pct(i.vendas_fechadas, i.meta_semana);
                const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
                return (
                  <TableRow key={i.id} data-testid={`funil-row-${i.id}`}>
                    <TableCell className="font-medium">{i.semana}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.predios_trabalhados)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.contatos_abordados)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.interessados)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.propostas_enviadas)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.vendas_fechadas)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.meta_semana)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={Math.min(p, 100)} className="h-2 flex-1" />
                        <span className={`text-xs font-semibold tabular-nums w-14 text-right ${tone}`}>{fmtPct(p)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`btn-edit-funil-${i.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(i.id)} data-testid={`btn-delete-funil-${i.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="dialog-funil">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar semana" : "Nova semana"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Semana *</Label>
              <Input data-testid="input-semana" value={form.semana} onChange={(e) => setF("semana", e.target.value)} placeholder="Ex: Semana 1 - Março/2026" />
            </div>
            <div className="space-y-1.5"><Label>Prédios trabalhados</Label>
              <Input type="number" min="0" value={form.predios_trabalhados} onChange={(e) => setF("predios_trabalhados", e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Contatos abordados</Label>
              <Input type="number" min="0" value={form.contatos_abordados} onChange={(e) => setF("contatos_abordados", e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Interessados</Label>
              <Input type="number" min="0" value={form.interessados} onChange={(e) => setF("interessados", e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Propostas enviadas</Label>
              <Input type="number" min="0" value={form.propostas_enviadas} onChange={(e) => setF("propostas_enviadas", e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Vendas fechadas</Label>
              <Input type="number" min="0" value={form.vendas_fechadas} onChange={(e) => setF("vendas_fechadas", e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Meta da semana</Label>
              <Input type="number" min="0" value={form.meta_semana} onChange={(e) => setF("meta_semana", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="btn-save-funil">{editing ? "Salvar alterações" : "Cadastrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
